import React from "react"
import { Route, Redirect } from "react-router-dom"
import { Query } from "@apollo/client/react/components"
import { LoadingComponent } from "@pga/pga-component-library"
import ErrorCard from "../components/ErrorCard"
import {
  map,
  any,
  equals,
  prop,
  isNil,
  propOr,
  propEq,
  path,
  isEmpty,
  pipe,
} from "ramda"
import { gql } from "@apollo/client"
import {
  LL_ROUTE_ACCESS_QUERY,
  toLegacyStatusCode,
  useLifelongLearningEnabled,
  useLifelongLearningQuery,
} from "../components/Education/lifelongLearning"

export const educationStatusQuery = gql`
  query CheckEducationStatus {
    me {
      id
      education {
        currentCppProgramStatus
        currentMppProgramStatus
      }
    }
  }
`

export const resolveRules = ({ memberCppStatus, memberMppStatus }) =>
  map(rule => {
    const memberHasCppStatus =
      typeof memberCppStatus === "string" && memberCppStatus !== ""
    const memberHasMppStatus =
      typeof memberMppStatus === "string" && memberMppStatus !== ""
    const specificStatusCodes = propOr([], "status", rule)
    const needToCheckSpecificCodes =
      Array.isArray(specificStatusCodes) && !isEmpty(specificStatusCodes)
    const shouldCheckCpp3 = propEq("programCode", "CPP3", rule)
    const shouldCheckMpp3 = propEq("programCode", "MPP3", rule)

    if (shouldCheckCpp3 && memberHasCppStatus) {
      return needToCheckSpecificCodes
        ? specificStatusCodes.includes(memberCppStatus)
        : true
    }

    if (shouldCheckMpp3 && memberHasMppStatus) {
      return needToCheckSpecificCodes
        ? specificStatusCodes.includes(memberMppStatus)
        : true
    }

    return false
  })

export const memberCanAccessRoute = ({ me, rules = [] }) =>
  pipe(
    resolveRules({
      memberCppStatus: path(["education", "currentCppProgramStatus"], me),
      memberMppStatus: path(["education", "currentMppProgramStatus"], me),
    }),
    any(equals(true)),
  )(rules)

const PROGRAM_KEY_BY_CODE = { SPP: "spp", CPP3: "cpp", MPP3: "mpp" }

// The server decides whether a member can view a program's progress; a rule
// that names specific statuses must also match the program's current status.
export const memberCanAccessLifelongLearningRoute = ({ me, rules = [] }) =>
  rules.some(rule => {
    const program =
      me?.lifelongLearning?.[PROGRAM_KEY_BY_CODE[rule.programCode]]
    if (!program?.eligibility?.canViewProgress) return false
    const specificStatusCodes = propOr([], "status", rule)
    return isEmpty(specificStatusCodes)
      ? true
      : specificStatusCodes.includes(toLegacyStatusCode(program.status))
  })

export const RouteOrRedirect =
  ({ me, Component, rules, canAccess = memberCanAccessRoute }) =>
  props =>
    canAccess({ me, rules }) ? (
      <Component {...props} />
    ) : (
      <Redirect to="/education/programs" />
    )

const V3CheckEducationStatus = ({
  component: Component,
  rules,
  ...routeProps
}) => (
  <Query query={educationStatusQuery}>
    {({ data, loading, error }) =>
      HandleQuery({ data, loading, error, Component, rules, routeProps })
    }
  </Query>
)

export const HandleQuery = ({
  data,
  loading,
  error,
  Component,
  rules,
  routeProps,
  canAccess,
}) => {
  if (loading) return <LoadingComponent />
  if (error) return <ErrorCard />

  const me = prop("me", data)

  return !isNil(me) ? (
    <Route
      {...routeProps}
      render={RouteOrRedirect({ me, rules, Component, canAccess })}
    />
  ) : null
}

const LifelongLearningRouteAccess = ({
  component: Component,
  rules,
  ...routeProps
}) => {
  const { data, loading, error } = useLifelongLearningQuery(
    LL_ROUTE_ACCESS_QUERY,
  )
  return HandleQuery({
    data,
    loading,
    error,
    Component,
    rules,
    routeProps,
    canAccess: memberCanAccessLifelongLearningRoute,
  })
}

const CheckEducationStatus = props =>
  useLifelongLearningEnabled() ? (
    <LifelongLearningRouteAccess {...props} />
  ) : (
    <V3CheckEducationStatus {...props} />
  )

export default CheckEducationStatus
